import React, {useEffect, useState} from 'react';
import PatientDashboardLayout from './PatientDashboardLayout';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faStethoscope, faUserMd} from '@fortawesome/free-solid-svg-icons';
import {useBaseUrl} from '../utils/utils';
import localForage from 'localforage';
import Swal from 'sweetalert2';

interface TreatmentRecord {
    assesment: string;
    complaint: string;
    allergies: string;
    physical: string;
    examined_by: string;
    date: string;
    medication: { name: string; dose: string; duration: string }[];
    follow_up: { observation: string; notes: string; conclusion: string }[];
}

const PatientTreatments: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [records, setRecords] = useState<TreatmentRecord[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "My Treatments - Uzima Healthcare";
        (async () => {
            try {
                const patientId = await localForage.getItem<string>('patient_id');
                if (!patientId) return;
                const token = await localForage.getItem<string>('access_token');
                const body = new URLSearchParams({patient_code: patientId});
                const response = await fetch(`${baseUrl}ehr/patient-records`, {
                    method: 'POST',
                    headers: {
                        Authorization: token ? `Bearer ${token}` : '',
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body,
                });
                const data = await response.json();
                if (data.status === 'success') {
                    setRecords(data.data || []);
                }
            } catch (error) {
                console.error('Error fetching treatment history:', error);
                Swal.fire('Error', 'Failed to load your treatment history', 'error');
            } finally {
                setLoading(false);
            }
        })();
    }, [baseUrl]);

    return (
        <PatientDashboardLayout>
            <div className="container-fluid">
                <div className="row mb-4">
                    <div className="col-12">
                        <h2 className="mb-1">
                            <FontAwesomeIcon icon={faStethoscope} className="me-2 text-primary"/>
                            My Treatments
                        </h2>
                        <p className="text-muted">Assessments, medications and follow-ups from your medical records</p>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : records.length === 0 ? (
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <FontAwesomeIcon icon={faStethoscope} size="3x" className="text-muted mb-3"/>
                            <h5>No Treatment History Yet</h5>
                            <p className="text-muted">Records from your consultations will appear here.</p>
                        </div>
                    </div>
                ) : (
                    records.map((record, idx) => (
                        <div key={idx} className="card mb-3">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <span>{new Date(record.date).toLocaleDateString()}</span>
                                <span className="text-muted small">
                                    <FontAwesomeIcon icon={faUserMd} className="me-1"/>
                                    {record.examined_by}
                                </span>
                            </div>
                            <div className="card-body">
                                <p><strong>Chief complaint:</strong> {record.complaint || '—'}</p>
                                <p><strong>Assessment:</strong> {record.assesment || '—'}</p>
                                {record.allergies && <p><strong>Allergies:</strong> {record.allergies}</p>}

                                {record.medication.length > 0 && (
                                    <>
                                        <h6 className="mt-3">Medication</h6>
                                        <ul className="mb-2">
                                            {record.medication.map((m, i) => (
                                                <li key={i}>{m.name} — {m.dose} ({m.duration})</li>
                                            ))}
                                        </ul>
                                    </>
                                )}

                                {record.follow_up.length > 0 && (
                                    <>
                                        <h6 className="mt-3">Follow-up</h6>
                                        <ul className="mb-0">
                                            {record.follow_up.map((f, i) => (
                                                <li key={i}>{f.observation} — {f.conclusion}</li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </PatientDashboardLayout>
    );
};

export default PatientTreatments;
